import React, { useState, useEffect } from 'react';
import SearchForm from './MoneyPrinter/SearchForm';
import EventsList from './MoneyPrinter/EventsList';
import EventDetail from './MoneyPrinter/EventDetail';
import ContestSelector from './MoneyPrinter/ContestSelector';
import OddsDisplay from './MoneyPrinter/OddsDisplay';
import OddsSelectionGrid from './MoneyPrinter/OddsSelectionGrid';
import ArbitrageResults from './MoneyPrinter/ArbitrageResults';
import { 
  extractSportFromEventName, 
  calculateArbitrage, 
  getContestOdds, 
  deriveAvailableContests 
} from './MoneyPrinter/utils';

const MoneyPrinter = () => {
  const [matchedEvents, setMatchedEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sport, setSport] = useState('nfl');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  // Navigation state
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Contest odds functionality state
  const [availableContests, setAvailableContests] = useState([]);
  const [selectedContests, setSelectedContests] = useState([]);
  const [liveOdds, setLiveOdds] = useState(null);
  const [oddsLoading, setOddsLoading] = useState(false);
  const [oddsError, setOddsError] = useState(null);

  // Grid selection state
  const [selectedBets, setSelectedBets] = useState([]);
  const [arbitrageResults, setArbitrageResults] = useState(null);

  const fetchContestPairings = async () => {
    setLoading(true);
    setError(null);
    setSelectedEvent(null); // Reset selected event when searching
    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (sport && sport !== 'all') params.append('sport', sport.toUpperCase());
      if (date) params.append('date', date);
      params.append('limit', '20'); // Optional limit
      
      const queryString = params.toString();
      const url = `https://1hqa0d4tqk.execute-api.us-east-1.amazonaws.com/prod/contest-pairings${queryString ? '?' + queryString : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setMatchedEvents(data.events || []);
      } else {
        setError('Failed to fetch contest pairings');
      }
    } catch (err) {
      setError(`Error fetching data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshOdds = async () => {
    if (selectedContests.length === 0) {
      setOddsError('Please select at least one contest');
      return;
    }

    setOddsLoading(true);
    setOddsError(null);
    
    try {
      const response = await fetch(
        'https://1hqa0d4tqk.execute-api.us-east-1.amazonaws.com/prod/refresh-odds',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contest_ids: selectedContests
          })
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setLiveOdds(data);
      } else {
        setOddsError(data.error || 'Failed to refresh odds');
      }
    } catch (err) {
      setOddsError(`Error refreshing odds: ${err.message}`);
    } finally {
      setOddsLoading(false);
    }
  };

  const handleContestSelection = (contestId) => {
    setSelectedContests(prev => 
      prev.includes(contestId)
        ? prev.filter(id => id !== contestId)
        : [...prev, contestId]
    );
  };

  const selectAllContests = () => {
    setSelectedContests(availableContests.map(contest => contest.contestId));
  };

  const clearAllContests = () => {
    setSelectedContests([]);
  };

  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    // Update available contests for this specific event
    const contests = deriveAvailableContests([event]);
    setAvailableContests(contests);
    
    // Clear previous state but don't auto-select or fetch
    setSelectedContests([]);
    setLiveOdds(null);
    setOddsError(null);
  };

  const handleBackToEvents = () => {
    setSelectedEvent(null);
    setAvailableContests([]);
    setSelectedContests([]);
    setLiveOdds(null);
    setSelectedBets([]);
    setArbitrageResults(null);
  };

  const handleCalculateArbitrage = (selectedBetData) => {
    setSelectedBets(selectedBetData);
    // The ArbitrageResults component will handle the calculation
    setArbitrageResults(selectedBetData);
  };

  useEffect(() => {
    fetchContestPairings();
  }, []);

  // Create a curried version of getContestOdds that has access to liveOdds
  const getContestOddsWithData = getContestOdds(liveOdds);

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h1>💰 MoneyPrinter</h1>
        <p>Sports Betting Arbitrage Opportunities</p>
      </div>

      <SearchForm
        sport={sport}
        setSport={setSport}
        date={date}
        setDate={setDate}
        loading={loading}
        onSearch={fetchContestPairings}
      />

      {/* Show Events List or Event Detail based on selection */}
      {!selectedEvent ? (
        <EventsList
          events={matchedEvents}
          loading={loading}
          error={error}
          onViewEvent={handleViewEvent}
          extractSportFromEventName={extractSportFromEventName}
        />
      ) : (
        <>
          {/* Event Header */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '20px',
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #dee2e6'
          }}>
            <button
              onClick={handleBackToEvents}
              style={{
                padding: '6px 12px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer',
                marginRight: '15px',
                fontSize: '0.8em'
              }}
            >
              ← Back to Events
            </button>
            <div>
              <h2 style={{ margin: '0 0 3px 0', color: '#333', fontSize: '1.3em' }}>
                {selectedEvent.event_name}
              </h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span style={{
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontSize: '0.7em'
                }}>
                  {extractSportFromEventName(selectedEvent.event_name)}
                </span>
                <span style={{ color: '#6c757d', fontSize: '0.8em' }}>
                  {new Date(selectedEvent.event_date).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Contest Selection and Manual Odds Refresh */}
          <ContestSelector
            availableContests={availableContests}
            selectedContests={selectedContests}
            loading={oddsLoading}
            error={oddsError}
            onContestSelection={handleContestSelection}
            onSelectAll={selectAllContests}
            onClearAll={clearAllContests}
            onRefreshOdds={refreshOdds}
          />

          {/* Odds Selection Grid */}
          <OddsSelectionGrid
            event={selectedEvent}
            liveOdds={liveOdds}
            getContestOdds={getContestOddsWithData}
            onCalculateArbitrage={handleCalculateArbitrage}
            loading={oddsLoading}
            error={oddsError}
          />

          {/* Arbitrage Results */}
          <ArbitrageResults
            selectedBets={selectedBets}
            arbitrageResults={arbitrageResults}
          />
        </>
      )}
    </div>
  );
};

export default MoneyPrinter;
